import {Fragment, useEffect, useState} from 'react'

import { CheckIcon, ClockIcon, XMarkIcon as XMarkIconMini } from '@heroicons/react/20/solid'
import {
    Button,
    Combobox,
    ComboboxButton,
    ComboboxInput,
    ComboboxOption,
    ComboboxOptions, Input, Select,
} from "@headlessui/react";
import {ChevronDownIcon, ChevronUpDownIcon} from "@heroicons/react/16/solid";
import {useAuth} from "../../contexts/AuthProvider";
import {db} from "../../firebase/firebase";
import { ReactComponent as LOGO } from "../../Logo.svg";
import {PlusIcon, ShoppingCartIcon, UserPlusIcon, XCircleIcon} from "@heroicons/react/24/outline";
import {numberWithCommas} from "../GlobalFunctions";
import firebase from "firebase/compat/app";

// Reusable QuantityInput component
const QuantityInput = ({ value, min = 1, max = 100, onChange }) => {
    const handleChange = (val) => {
        let num = parseInt(val);
        if (isNaN(num)) num = min;
        if (num < min) num = min;
        if (num > max) num = max;
        onChange(num);
    };

    return (
        <div className="flex items-center border rounded-md overflow-hidden w-24">
            <button
                type="button"
                onClick={() => handleChange((value ?? min) - 1)}
                className="px-2 py-1 text-gray-500 hover:bg-gray-100"
            >
                -
            </button>

            <input
                type="number"
                value={value ?? 1}
                onChange={(e) => handleChange(e.target.value)}
                className="w-full text-center text-sm outline-none"
            />

            <button
                type="button"
                onClick={() => handleChange((value ?? min) + 1)}
                className="px-2 py-1 text-gray-500 hover:bg-gray-100"
            >
                +
            </button>
        </div>
    );
};


const OrderForm = () => {

    const { currentUser } = useAuth();

    const [open, setOpen] = useState(false);

    const [shoppers, setShoppers] = useState([]);
    const [products, setProducts] = useState([]);

    const [productsList, setProductsList] = useState([]);

    useEffect(() => {
        const fetchCustomers = async () => {
            if (currentUser) {
                try {
                    const storedInventoryData = localStorage.getItem(`customers_${currentUser.uid}`);
                    if (storedInventoryData) {
                        const inventoryData = JSON.parse(storedInventoryData);
                        setShoppers(inventoryData);
                    } else {
                        const inventoryCollectionRef = db.collection('Customers');
                        const snapshot = await inventoryCollectionRef.get();

                        if (!snapshot.empty) {
                            const inventoryData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                            setShoppers(inventoryData);
                            localStorage.setItem(`customers_${currentUser.uid}`, JSON.stringify(inventoryData));
                        } else {
                            console.log('No customers found');
                        }
                    }
                } catch (error) {
                    console.error('Error fetching inventory:', error);
                }
            }
        };

        const fetchInventory = async () => {
            if (currentUser) {
                try {
                    const storedInventoryData = localStorage.getItem(`inventory_${currentUser.uid}`);
                    if (storedInventoryData) {
                        const inventoryData = JSON.parse(storedInventoryData);
                        setProducts(inventoryData);
                    } else {
                        const inventoryCollectionRef = db.collection('Inventory');
                        const snapshot = await inventoryCollectionRef.get();

                        if (!snapshot.empty) {
                            const inventoryData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                            setProducts(inventoryData);
                            localStorage.setItem(`inventory_${currentUser.uid}`, JSON.stringify(inventoryData));
                        } else {
                            console.log('No inventory found');
                        }
                    }
                } catch (error) {
                    console.error('Error fetching inventory:', error);
                }
            }
        };

        fetchCustomers();
        fetchInventory();

    }, [currentUser]);


    const [query, setQuery] = useState('');
    const [selectedPerson, setSelectedPerson] = useState(null);

    const filteredPeople =
        query === ''
            ? shoppers
            : shoppers.filter((person) => {
                return person.name.toLowerCase().includes(query.toLowerCase())
            });


    const [productQuery, setProductQuery] = useState('');
    const [selectedProduct, setSelectedProduct] = useState(null);

    const filteredProducts =
        productQuery === ''
            ? products
            : products.filter((item) => {
                return item.name.toLowerCase().includes(productQuery.toLowerCase())
            });

    const calculateTotalCost = (productsList) => {
        return productsList.reduce((total, product) => {
            const unitPrice = parseFloat(product.unitPrice) || 0;
            const orderQuant = parseInt(product.orderQuant) || 0;
            return total + unitPrice * orderQuant;
        }, 0);
    };

    const handleSaveTransaction = async () => {
        try {
            if (!selectedPerson || productsList.length === 0) return;

            const orderPayload = {
                shopperId: selectedPerson.id,
                shopperName: selectedPerson.name,
                items: productsList,
                total: calculateTotalCost(productsList),
                createdAt: Date.now(),
            };

            // Create order
            const orderRef = await db.collection("Transactions").add(orderPayload);

            // Update inventory quantities
            const batch = db.batch();

            productsList.forEach((item) => {
                const productRef = db.collection("Inventory").doc(item.id);
                const newQuantity = (item.quantity || 0) - (item.orderQuant || 0);
                batch.update(productRef, {
                    quantity: newQuantity < 0 ? 0 : newQuantity,
                });
            });

            await batch.commit();

            // Add order ID to shopper
            await db.collection("Customers").doc(selectedPerson.id).update({
                orders: firebase.firestore.FieldValue.arrayUnion(orderRef.id),
            });

            // Clear form
            setProductsList([]);
            setSelectedPerson(null);
            setSelectedProduct(null);
            setProductQuery('');
            setQuery('');

            console.log("Transaction saved successfully");
        } catch (err) {
            console.error("Error saving transaction:", err);
        }
    };

    return (
        <div className="px-4 pt-4 animate-fadeIn h-full overflow-y-auto rounded-lg shadow-lg">


            <main className="grid grid-cols-3 gap-4">

                {/*Cart*/}
                <div className={`col-span-2`}>

                    {/*Products*/}
                    <section aria-labelledby="cart-heading" className="bg-gray-50 p-4 rounded-lg space-y-2">

                        <h2 id="summary-heading" className="text-lg font-semibold border-b pb-1">
                            Shopping Cart Contents
                        </h2>

                        <div className={`text-lg font-normal flex items-center`}>
                            <Combobox
                                as="div"
                                className={`w-full`}
                                value={selectedProduct}
                                onChange={(product) => {
                                    setProductQuery('')
                                    setSelectedProduct(product)
                                }}
                            >
                                <div className="relative">
                                    <ComboboxInput
                                        className="block w-full border rounded-md bg-white py-1.5 pr-12 pl-3 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-600 sm:text-sm/6"
                                        onChange={(event) => setProductQuery(event.target.value)}
                                        onBlur={() => setProductQuery('')}
                                        placeholder={`Select a product of the ${products.length} products in stock`}
                                        displayValue={(product) => product?.name}
                                    />
                                    <ComboboxButton
                                        className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-hidden">
                                        <ChevronUpDownIcon className="size-5 text-gray-400" aria-hidden="true"/>
                                    </ComboboxButton>

                                    {filteredProducts.length > 0 && (
                                        <ComboboxOptions
                                            className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm">
                                            {filteredProducts.map((product) => (
                                                <ComboboxOption
                                                    key={product.id}
                                                    value={product}
                                                    onMouseDown={() => {
                                                        setProductsList((prevList) => {
                                                            const alreadyExists = prevList.some((p) => p.id === product.id);
                                                            if (alreadyExists) return prevList;

                                                            // Add product with orderQuant field and maxQuantity
                                                            return [...prevList, {...product, orderQuant: 1, maxQuantity: product.quantity}];
                                                        });
                                                    }}
                                                    className="group relative cursor-default py-2 pr-9 pl-3 text-gray-900 select-none data-focus:bg-indigo-600 data-focus:text-white data-focus:outline-hidden"
                                                >
                                                    <span
                                                        className="block truncate group-data-selected:font-semibold">{product.name}</span>

                                                    <span
                                                        className="absolute inset-y-0 right-0 hidden items-center pr-4 text-indigo-600 group-data-focus:text-white group-data-selected:flex">
                                              <CheckIcon className="size-5" aria-hidden="true"/>
                                            </span>
                                                </ComboboxOption>
                                            ))}
                                        </ComboboxOptions>
                                    )}
                                </div>
                            </Combobox>
                        </div>

                        <div className={`h-[calc(100dvh-9.5rem)] overflow-y-auto`}>
                            {productsList.length === 0 ? (

                                <div className={`w-full h-full flex items-center justify-center`}>

                                    <div className={`text-center flex flex-col items-center`}>
                                        <ShoppingCartIcon className={`text-gray-400 size-16 animate-heartbeat`}/>
                                        <h3 className="mt-2 text-md font-semibold">Your Cart Is Empty</h3>
                                        <h1 className="text-xs">Select A Product From the Inventory Above!</h1>

                                    </div>

                                </div>

                            ) : (

                                <ul role="list" className="divide-y divide-gray-200 border-b border-t border-gray-200">
                                    {productsList.map((product, productIdx) => (
                                        <li key={product?.id} className="flex items-center py-2 animate-slideInRight">

                                            <div className="shrink-0">
                                                <LOGO className="size-12 rounded-md object-cover sm:size-12"/>
                                            </div>

                                            <div className="ml-4 flex flex-1 flex-col justify-between sm:ml-6">
                                                <div
                                                    className="relative pr-9 sm:grid sm:grid-cols-2 sm:gap-x-6 sm:pr-0">
                                                    <div>
                                                        <div className="flex justify-between">
                                                            <h3 className="text-sm">
                                                                <div
                                                                    className="font-medium text-gray-700 hover:text-gray-800">
                                                                    {product?.name}
                                                                </div>
                                                            </h3>
                                                        </div>

                                                        <p className="mt-1 text-sm text-gray-500">$ <span
                                                            className={`text-lg font-semibold text-black`}>{product?.unitPrice}</span> USD
                                                        </p>
                                                    </div>

                                                    <div className="mt-4 sm:mt-0 sm:pr-9">

                                                        <div className="inline-grid w-full max-w-16 grid-cols-1">
                                                            <QuantityInput
                                                                value={product.orderQuant}
                                                                min={1}
                                                                max={product.maxQuantity ?? product.quantity}
                                                                onChange={(newQuant) => {
                                                                    setProductsList((prevList) =>
                                                                        prevList.map((p, i) =>
                                                                            i === productIdx ? { ...p, orderQuant: newQuant } : p
                                                                        )
                                                                    );
                                                                }}
                                                            />
                                                        </div>

                                                        <p className="mt-1 text-sm text-gray-500">$ <span
                                                            className={`text-lg font-semibold text-black`}>{numberWithCommas(product?.unitPrice * product?.orderQuant)}</span> USD
                                                        </p>

                                                        <div className="absolute right-0 top-0">
                                                            <Button type="button"
                                                                    onClick={() =>
                                                                        setProductsList((prevList) =>
                                                                            prevList.filter((p) => p.id !== product.id)
                                                                        )
                                                                    }
                                                                    className="-m-2 inline-flex p-2 text-gray-400 hover:text-gray-500">
                                                                <span className="sr-only">Remove</span>
                                                                <XMarkIconMini aria-hidden="true" className="size-5"/>
                                                            </Button>
                                                        </div>

                                                    </div>
                                                </div>

                                                <p className="flex space-x-2 text-sm text-gray-700">
                                                    {product?.quantity > 0 ? (
                                                        <CheckIcon aria-hidden="true"
                                                                   className="size-5 shrink-0 text-green-500"/>
                                                    ) : (
                                                        <ClockIcon aria-hidden="true"
                                                                   className="size-5 shrink-0 text-gray-300"/>
                                                    )}

                                                    <span>{product?.quantity > 0 ? 'In stock' : `Out of Stock`}</span>
                                                </p>
                                            </div>

                                        </li>
                                    ))}
                                </ul>

                            )}
                        </div>


                    </section>

                </div>

                {/*Sidebar*/}
                <div className={`col-span-1 space-y-4`}>

                    {/* Shopper Section */}
                    <section
                        aria-labelledby="summary-heading"
                        className="rounded-lg bg-gray-50 p-4 lg:col-span-5 lg:mt-0 space-y-4"
                    >
                        <h2 id="summary-heading" className="text-lg border-b pb-1 font-semibold">
                            Shopper Select
                        </h2>

                        <div className={`text-lg font-normal flex items-center`}>
                            <Combobox
                                as="div"
                                className="w-full"
                                value={selectedPerson}
                                onChange={(person) => {
                                    setQuery('')
                                    setSelectedPerson(person)
                                }}
                            >
                                <div className="relative">
                                    <ComboboxInput
                                        className="block w-full border rounded-md bg-white py-1.5 pr-12 pl-3 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-600 sm:text-sm/6"
                                        placeholder={`Select a Shopper of the ${shoppers.length} shoppers in the system`}
                                        onChange={(event) => setQuery(event.target.value)}
                                        onBlur={() => setQuery('')}
                                        displayValue={(person) => person?.name}
                                    />
                                    <ComboboxButton
                                        className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-hidden">
                                        <ChevronUpDownIcon className="size-5 text-gray-400" aria-hidden="true"/>
                                    </ComboboxButton>

                                    {filteredPeople.length > 0 && (
                                        <ComboboxOptions
                                            className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm">
                                            {filteredPeople.map((person) => (
                                                <ComboboxOption
                                                    key={person.id}
                                                    value={person}
                                                    className="group relative cursor-default py-2 pr-9 pl-3 text-gray-900 select-none data-focus:bg-indigo-600 data-focus:text-white data-focus:outline-hidden"
                                                >
                                                    <span
                                                        className="block truncate group-data-selected:font-semibold">{person.name}</span>

                                                    <span
                                                        className="absolute inset-y-0 right-0 hidden items-center pr-4 text-indigo-600 group-data-focus:text-white group-data-selected:flex">
                                              <CheckIcon className="size-5" aria-hidden="true"/>
                                            </span>
                                                </ComboboxOption>
                                            ))}
                                        </ComboboxOptions>
                                    )}
                                </div>
                            </Combobox>
                        </div>

                        {selectedPerson && (
                            <div className="p-3 bg-white border border-gray-200 rounded-md space-y-2">

                                <div className={`flex items-center justify-between`}>
                                    <h3 className="text-sm font-semibold text-gray-700 border-b pb-1">
                                        Shopper Details
                                    </h3>

                                    {selectedPerson.orders && (
                                        <div className="text-xs text-gray-500">
                                            <span className="font-medium text-gray-700">Orders:</span>{" "}
                                            {selectedPerson.orders.length}
                                        </div>
                                    )}
                                </div>

                                <div className="text-xs text-gray-500">
                                    <span className="font-medium text-gray-700">Name:</span>{" "}
                                    {selectedPerson.name}
                                </div>

                                {selectedPerson.email && (
                                    <div className="text-xs text-gray-500">
                                        <span className="font-medium text-gray-700">Email:</span>{" "}
                                        {selectedPerson.email}
                                    </div>
                                )}

                                {selectedPerson.phone && (
                                    <div className="text-xs text-gray-500">
                                        <span className="font-medium text-gray-700">Phone:</span>{" "}
                                        {selectedPerson.phone}
                                    </div>
                                )}

                                {selectedPerson.id && (
                                    <div className="text-xs text-gray-500">
                                        <span className="font-medium text-gray-700">Phone:</span>{" "}
                                        {selectedPerson.id}
                                    </div>
                                )}

                                {selectedPerson.childrenList && (
                                    <div className="text-xs text-gray-500">
                                        <span className="font-medium text-gray-700">Children:</span>{" "}
                                        {selectedPerson.childrenList.length}
                                    </div>
                                )}

                            </div>
                        )}

                    </section>

                    {/* Order summary */}
                    <section
                        aria-labelledby="summary-heading"
                        className="mt-16 rounded-lg bg-gray-50 p-4 lg:col-span-5"
                    >
                        <h2 id="summary-heading" className="text-lg border-b pb-1 font-semibold">
                            Order Summary
                        </h2>

                        <dl className="mt-6 space-y-4">
                            <div className="flex items-center justify-between">
                                <dt className="text-sm text-gray-600">Subtotal</dt>
                                <dd className="text-sm font-medium text-gray-500">$ <span
                                    className={`text-lg text-black font-semibold`}>{numberWithCommas(calculateTotalCost(productsList).toFixed(2))}</span> USD
                                </dd>
                            </div>
                            <div className="flex items-center justify-between border-t border-gray-200 pt-4">
                                <dt className="flex items-center text-sm text-gray-600">
                                    <span>Size</span>
                                </dt>
                                <dd className="text-sm font-medium text-gray-900">{productsList.length} Items</dd>
                            </div>

                            <div className="flex items-center justify-between border-t border-gray-200 pt-4">
                                <dt className="text-base font-medium text-gray-900">Order total</dt>
                                <dd className="text-base font-medium text-gray-500">$ <span
                                    className={`text-lg text-black font-semibold`}>{numberWithCommas(calculateTotalCost(productsList).toFixed(2))}</span> USD
                                </dd>
                            </div>
                        </dl>

                        <div className="mt-6">
                            <Button
                                type="button"
                                onClick={handleSaveTransaction}
                                disabled={!selectedPerson}
                                className="w-full rounded-md border border-transparent bg-blue-600 px-4 py-3 text-base font-medium text-white shadow-sm hover:bg-opacity-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-50"
                            >
                                {selectedPerson ? 'Save Transaction' : 'Select Shopper'}
                            </Button>
                        </div>
                    </section>

                </div>

            </main>

        </div>
    )
}

export default OrderForm;
